import {
  toWSDateString,
  WSAlert,
  WSButton,
  WSElement,
  WSFlexBox,
  WSSidebar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  BillingFeeItemType,
  Currency,
  GetInvoiceExpandParameterInner,
  PayableStatus,
  PayeeReviewStatus
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import sortBy from "lodash/sortBy";
import React, { useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Card } from "../../../ClientPayments/components/Card/Card";
import { queriesAccounts, queriesPayables } from "@query";
import { useXWingspanPayerId } from "../../../modules/Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { selectorAccountDisplayName } from "../../../shared/selectors/selectorAccountDisplayName";
import { selectorPayeeDisplayName } from "../../../shared/selectors/selectorPayeeDisplayName";
import { WSQueries } from "../../../query/WSQuery";
import { getParentPath } from "../../../utils/goToParentRoute";
import { PayableActivity } from "../../components/PayableActivity";
import { PayableDetailsHeader } from "../../components/PayableDetailsHeader";
import { PayableParentInvoice } from "../../components/PayableParentInvoice";
import { PayableWorkLog } from "../../components/PayableWorkLog";
import { CancelledPayableWorkLogInfo } from "../../../modules/WorkLogs/components/CancelledPayableWorkLogInfo";
import { QuickbooksHistory } from "../../components/QuickbooksHistory/QuickbooksHistory";
import { QuickbooksWrapper } from "../../components/QuickbooksWrapper/QuickbooksWrapper";
import { Totals, TotalsSection } from "../../components/Totals/Totals";
import styles from "../InvoicesInvoiceDetails.module.scss";

type Props = RouteComponentProps<
  { payableId: string; year: string },
  {},
  { backPath?: string }
>;

export const PayableDetails: React.FC<Props> = ({
  match,
  history,
  location
}) => {
  const backPath = location.state?.backPath || getParentPath(history);
  const onBack = () => {
    if (backPath) {
      history.push({
        pathname: backPath,
        search: location.search,
        state: {
          ...(location.state ?? {})
        }
      });
    }
  };

  return (
    <WSSidebar.Container onClose={onBack}>
      <Inner payableId={match.params.payableId} />
    </WSSidebar.Container>
  );
};

type InnerProps = {
  payableId: string;
};

// v3 inlines a related party only when the read asks for it, and only the
// perspective the caller can retrieve directly: a payer reading its own payable
// gets `payee` and never `payer` (the opposite, inaccessible perspective is
// omitted). Module-level so the query key stays stable across renders — note a
// `Set` hashes to `{}` in the cache key, so two reads of the same payable that
// ask for different expansions would share one entry.
const PAYABLE_EXPAND = new Set([GetInvoiceExpandParameterInner.PAYEE]);

const Inner: React.FC<InnerProps> = ({ payableId }) => {
  const [detailsVisible, setDetailsVisible] = useState(true);

  const xWingspanAccount = useXWingspanPayerId();
  const queryPayable = queriesPayables.getPayable.useQuery({
    payableId,
    xWingspanAccount,
    expand: PAYABLE_EXPAND
  });

  // "Bill to" is the payer account this payable belongs to. The v3 payable never
  // inlines the `payer` perspective for a payer caller, so the name is read off
  // the account (replaces the v1 `wsName({ user, member })` derivation).
  const queryPayerAccount = queriesAccounts.getAccount.useQuery(
    { accountId: queryPayable.data?.payerAccountId! },
    { enabled: !!queryPayable.data?.payerAccountId, retry: false }
  );

  return (
    <WSQueries queries={{ queryPayable }}>
      {({ queryPayable: { data: payable } }) => {
        const payee = payable.payee;
        const payeeName = payee ? selectorPayeeDisplayName(payee) : "";
        const payerName = queryPayerAccount.data
          ? selectorAccountDisplayName(queryPayerAccount.data)
          : "";
        const currency = (payable.currency || "USD") as Currency;

        const payableLineItems = sortBy(payable.lineItems, lineItem =>
          lineItem.metadata?.bulkImporterItemNumber
            ? Number(lineItem.metadata.bulkImporterItemNumber)
            : undefined
        );

        const amount = payable.amount ?? 0;
        const deductionsTotal = payable.amountDetails?.deductions ?? 0;
        const lateFeeTotal = (payable.fees?.items ?? [])
          .filter(item => item.type === BillingFeeItemType.LATE)
          .reduce((sum, item) => sum + (item.amount ?? 0), 0);

        const totalsSections: TotalsSection[] = [
          {
            items: payableLineItems.map(lineItem => [
              lineItem.description || "",
              formatMoney(lineItem.totalCost ?? 0, currency)
            ])
          },
          {
            items: [
              {
                bold: true,
                left: "Total",
                right: formatMoney(amount, currency)
              }
            ]
          }
        ];

        if (deductionsTotal > 0) {
          totalsSections.push({
            items: [
              {
                left: "Deductions",
                right: formatMoney(-deductionsTotal, currency)
              },
              {
                bold: true,
                left: "Contractor gross income",
                right: formatMoney(amount - deductionsTotal, currency)
              }
            ]
          });
        }

        const isDisputed =
          payable.payeeReviewStatus === PayeeReviewStatus.DISPUTED;
        const metadata = payable.metadata ?? {};

        return (
          <WSSidebar.Layout
            header={
              payable.externalId ? `Invoice #${payable.externalId}` : "Invoice"
            }
          >
            <WSElement className={styles.main}>
              <Card mb="XL">
                <WSText.Heading4 mb="M">{payeeName}</WSText.Heading4>

                {payable.status === PayableStatus.CANCELLED && (
                  <CancelledPayableWorkLogInfo payable={payable} />
                )}

                <PayableDetailsHeader payable={payable} payee={payee} />

                {payable.status === PayableStatus.PENDING ? (
                  <WSAlert
                    theme="warning"
                    icon="alert-circle"
                    title="Not yet eligible for payments"
                    mt="XL"
                  >
                    Your invite was sent. We're still waiting for this person to
                    complete their onboarding requirements before they can
                    receive payments.
                  </WSAlert>
                ) : null}

                {isDisputed && (
                  <WSAlert
                    theme="info"
                    icon="info-circle"
                    title="About disputed invoices"
                    mt="XL"
                  >
                    A contractor has disputed this invoice. Review their reason
                    in "All Activity," then approve, edit, or cancel the
                    invoice. As the payer, you have final approval.
                  </WSAlert>
                )}
              </Card>

              <Card mb="XL">
                <WSFlexBox wrap="nowrap" justify="space-between">
                  <WSButton.Link
                    onClick={() => {
                      setDetailsVisible(!detailsVisible);
                    }}
                    rightIcon={detailsVisible ? "caret-up" : "caret-down"}
                  >
                    {detailsVisible ? "Hide" : "Show"} invoice details
                  </WSButton.Link>
                  <WSText.Heading5>
                    {formatMoney(amount, currency)}
                  </WSText.Heading5>
                </WSFlexBox>
                {detailsVisible && (
                  <WSElement mt="XL">
                    <WSElement mb="XL">
                      <WSText.ParagraphSm mb="XS" color="gray500">
                        Bill to
                      </WSText.ParagraphSm>
                      <WSText mb="XL">{payerName}</WSText>

                      <WSText.ParagraphSm mb="XS" color="gray500">
                        From
                      </WSText.ParagraphSm>
                      <WSText>{payeeName}</WSText>
                    </WSElement>

                    <WSElement mb="XL">
                      <WSText.ParagraphSm mb="XS" color="gray500">
                        Due date
                        {!!payable.scheduledPaymentDate && " (updated)"}
                      </WSText.ParagraphSm>
                      <WSText>
                        {toWSDateString(
                          payable.scheduledPaymentDate || payable.dueDate,
                          "monthDayYear"
                        )}
                      </WSText>
                    </WSElement>
                    {metadata.purchaseOrderNumber ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          PO Number
                        </WSText.ParagraphSm>
                        <WSText>{metadata.purchaseOrderNumber}</WSText>
                      </WSElement>
                    ) : null}
                    {metadata.projectName ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Project Name
                        </WSText.ParagraphSm>
                        <WSText>{metadata.projectName}</WSText>
                      </WSElement>
                    ) : null}
                    {payable.notes ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Notes
                        </WSText.ParagraphSm>
                        <WSText>{payable.notes}</WSText>
                      </WSElement>
                    ) : null}
                    {lateFeeTotal > 0 ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Late fee
                        </WSText.ParagraphSm>
                        <WSText>{formatMoney(lateFeeTotal, currency)}</WSText>
                      </WSElement>
                    ) : null}

                    <WSElement mb="XL">
                      <PayableWorkLog payable={payable} />
                    </WSElement>

                    {/*
                      Attachment list disabled: rendering a row resolves the
                      file through the retired v1 member-files service, which
                      throws `NotMigrated`. v3 exposes the attachment metadata
                      on the payable but no way to resolve a member-file to a
                      name or download URL. Restore with the upload (see the
                      attachments block in OtherSection).
                    */}
                    {/*
                    {(payable.attachments?.data ?? []).length > 0 ? (
                      <WSElement mb="XL">
                        <WSText.ParagraphSm mb="XS" color="gray500">
                          Attachments
                        </WSText.ParagraphSm>
                        {(payable.attachments?.data ?? []).map(attachment =>
                          attachment.memberFileId ? (
                            <PublicAttachmentDownloadLink
                              key={attachment.id}
                              id={attachment.memberFileId}
                              mb="M"
                            />
                          ) : null
                        )}
                      </WSElement>
                    ) : null}
                    */}

                    <Totals sections={totalsSections} />
                  </WSElement>
                )}

                {payable.parentInvoiceId && (
                  <WSElement mt="2XL">
                    <PayableParentInvoice
                      parentInvoiceId={payable.parentInvoiceId}
                    />
                  </WSElement>
                )}
              </Card>

              <Card>
                <WSText.Heading5 mb="XL">All activity</WSText.Heading5>
                <PayableActivity payable={payable} />
              </Card>
              <QuickbooksWrapper>
                <QuickbooksHistory
                  Wrapper={Card}
                  mt="XL"
                  entityId={payable.id!}
                />
              </QuickbooksWrapper>
            </WSElement>
          </WSSidebar.Layout>
        );
      }}
    </WSQueries>
  );
};
